import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Typography, Spin, Space } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const RANK_COLOR = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };

function AgentStatBox({ title, rows }) {
  const campaigns = [...new Set(rows.map(r => r.CampaignName))].sort();
  return (
    <Card
      title={<Space><TrophyOutlined />{title}</Space>}
      size="small"
      styles={{ body: { padding: 0 } }}
      style={{ height: '100%' }}
    >
      {campaigns.map((camp, idx) => (
        <div key={camp} style={{ marginTop: idx === 0 ? 0 : 12 }}>
          <div style={{ display: 'flex', padding: '4px 12px', background: 'var(--ant-color-fill-quaternary)', gap: 8, alignItems: 'center' }}>
            <span style={{ minWidth: 22 }} />
            <span style={{ flex: 1, fontWeight: 600, fontSize: 12 }}>{camp}</span>
            <span style={{ fontSize: 10, color: 'var(--ant-color-text-secondary)', width: 52, textAlign: 'right' }}>Calls</span>
            <span style={{ fontSize: 10, color: 'var(--ant-color-text-secondary)', width: 82, textAlign: 'right' }}>First Call</span>
            <span style={{ fontSize: 10, color: 'var(--ant-color-text-secondary)', width: 82, textAlign: 'right' }}>Last Call</span>
          </div>
          {rows.filter(r => r.CampaignName === camp).map(r => (
            <div
              key={r.Rank}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '3px 12px',
                gap: 8,
                borderBottom: '1px solid var(--ant-color-split)',
              }}
            >
              <span style={{
                minWidth: 22,
                fontWeight: 700,
                color: RANK_COLOR[r.Rank] ?? 'var(--ant-color-text-secondary)',
                fontSize: 13,
              }}>
                {r.Rank}
              </span>
              <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.AgentName ?? r.AgentNo}
              </span>
              <span style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--ant-color-text-secondary)', width: 52, textAlign: 'right' }}>
                {r.Qty.toLocaleString()}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)', width: 82, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {r.FirstContact ?? '—'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--ant-color-text-secondary)', width: 82, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {r.LastContact ?? '—'}
              </span>
            </div>
          ))}
        </div>
      ))}
      {rows.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ant-color-text-secondary)' }}>No data</div>
      )}
    </Card>
  );
}

function TotalBox({ title, allTimeRows, yearRows, year }) {
  const campaigns = [...new Set([...allTimeRows, ...yearRows].map(r => r.CampaignName))].sort();
  const allTimeMap = Object.fromEntries(allTimeRows.map(r => [r.CampaignName, r.Qty]));
  const yearMap    = Object.fromEntries(yearRows.map(r => [r.CampaignName, r.Qty]));

  const columns = [
    { title: 'Campaign', dataIndex: 'camp', key: 'camp', render: v => <Text style={{ fontSize: 12 }}>{v}</Text> },
    {
      title: 'All Time', dataIndex: 'allTime', key: 'allTime', align: 'right',
      render: v => <Text style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{v != null ? v.toLocaleString() : '-'}</Text>,
    },
    {
      title: year, dataIndex: 'yr', key: 'yr', align: 'right',
      render: v => <Text style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{v != null ? v.toLocaleString() : '-'}</Text>,
    },
  ];

  const tableData = campaigns.map(camp => ({
    key: camp,
    camp,
    allTime: allTimeMap[camp] ?? null,
    yr: yearMap[camp] ?? null,
  }));

  return (
    <Card
      title={title}
      size="small"
      style={{ height: '100%' }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        style={{ fontSize: 12 }}
      />
    </Card>
  );
}

export default function WallOfFamePage() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    api.get('/api/wall-of-fame')
      .then(r => { setData(Array.isArray(r.data) ? r.data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const byStat = {};
  data.forEach(r => {
    if (!byStat[r.Stat]) byStat[r.Stat] = [];
    byStat[r.Stat].push(r);
  });

  const get = key => byStat[key] ?? [];

  const agentRows = [
    {
      label: 'All Time',
      stats: [
        { key: 'All Time Inbound',                      label: 'All Time Inbound' },
        { key: 'All Time Inbound & Outbound Attempted', label: 'All Time Inbound & Outbound Attempted' },
        { key: 'All Time Support Calls Handled',        label: 'All Time Support Calls Handled' },
      ],
    },
    {
      label: `${year}`,
      stats: [
        { key: `${year} Inbound`,                      label: `${year} Inbound` },
        { key: `${year} Inbound & Outbound Attempted`, label: `${year} Inbound & Outbound Attempted` },
        { key: `${year} Support Calls Handled`,        label: `${year} Support Calls Handled` },
      ],
    },
  ];

  const totalBoxes = [
    {
      title: 'Inbound — Campaign Totals',
      allTimeKey: 'All Time Inbound Total',
      yearKey: `${year} Inbound Total`,
    },
    {
      title: 'Inbound & Outbound — Campaign Totals',
      allTimeKey: 'All Time Inbound & Outbound Attempted Total',
      yearKey: `${year} Inbound & Outbound Attempted Total`,
    },
    {
      title: 'Support Calls — Campaign Totals',
      allTimeKey: 'All Time Support Calls Handled Total',
      yearKey: `${year} Support Calls Handled Total`,
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 20px' }}>
      <Title level={3} style={{ marginBottom: 20 }}>
        <TrophyOutlined style={{ marginRight: 8 }} />Wall of Fame
      </Title>

      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>
          All Time — Top 10 All Campaigns
        </Text>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={8}><AgentStatBox title="All Time Inbound All Campaigns"                      rows={get('All Time Inbound All Campaigns')} /></Col>
          <Col xs={24} md={8}><AgentStatBox title="All Time Inbound & Outbound Attempted All Campaigns" rows={get('All Time Inbound & Outbound Attempted All Campaigns')} /></Col>
          <Col xs={24} md={8}><AgentStatBox title="All Time Support Calls Handled All Campaigns"        rows={get('All Time Support Calls Handled All Campaigns')} /></Col>
        </Row>
      </div>

      {agentRows.map(rowDef => (
        <div key={rowDef.label} style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>
            {rowDef.label} — Top 10 by Campaign
          </Text>
          <Row gutter={[12, 12]}>
            {rowDef.stats.map(s => (
              <Col key={s.key} xs={24} md={8}>
                <AgentStatBox title={s.label} rows={get(s.key)} />
              </Col>
            ))}
          </Row>
        </div>
      ))}

      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 8 }}>
          Campaign Totals
        </Text>
        <Row gutter={[12, 12]}>
          {totalBoxes.map(box => (
            <Col key={box.title} xs={24} md={8}>
              <TotalBox
                title={box.title}
                allTimeRows={get(box.allTimeKey)}
                yearRows={get(box.yearKey)}
                year={year}
              />
            </Col>
          ))}
        </Row>
      </div>
    </div>
  );
}
